import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from './app/AppShell';
import { requireRoute } from './lib/route-guards';
import { isAuthenticated } from './lib/token-store';
import { AuditLogPage } from './routes/AuditLogPage';
import { ConfigPage } from './routes/ConfigPage';
import { ForbiddenPage } from './routes/ForbiddenPage';
import { DashboardPage } from './routes/DashboardPage';
import { FirmDetailPage } from './routes/FirmDetailPage';
import { FirmsPage } from './routes/FirmsPage';
import { HelpPage } from './routes/HelpPage';
import { LoginPage } from './routes/LoginPage';
import { NewSalePage } from './routes/NewSalePage';
import { ForgotPasswordPage, ResetPasswordPage } from './routes/PasswordResetPages';
import { SaleDetailPage } from './routes/SaleDetailPage';
import { SalesListPage } from './routes/SalesListPage';
import { VehicleFormPage } from './routes/VehicleFormPage';
import { VehiclesListPage } from './routes/VehiclesListPage';
import { SubmissionsPage } from './routes/SubmissionsPage';
import { UserDetailPage } from './routes/UserDetailPage';
import { UsersPage } from './routes/UsersPage';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// Password reset (M19) sits beside `/login`, outside the auth guard: someone who cannot sign
// in is precisely who needs it.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

// Pathless layout with the auth guard — everything under it requires a session.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: '/login' });
  },
  component: AppShell,
});

// The admin home is the overview, not a redirect into the sale list: landing on the list made
// an operator count rows across three tabs to answer "how many auctions are running".
const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: DashboardPage,
  beforeLoad: requireRoute('/'),
});

const salesListRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sales',
  component: SalesListPage,
  beforeLoad: requireRoute('/sales'),
});

const newSaleRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sales/new',
  component: NewSalePage,
  beforeLoad: requireRoute('/sales/new'),
});

const saleDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sales/$saleId',
  component: SaleDetailPage,
  beforeLoad: requireRoute('/sales/$saleId'),
});

// Search params are validated so a pool filter is shareable — `/vehicles?saleId=…` ("which
// cars are in this auction") is the link an operator sends a colleague, the same typed
// pattern the audit route uses.
const vehiclesListRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vehicles',
  component: VehiclesListPage,
  beforeLoad: requireRoute('/vehicles'),
  // The three pickers, and only those. The pool's search box and year range are deliberately
  // **not** addressable (see the page's header comment for why), so accepting them here would
  // let the URL carry a filter nothing keeps in step — do not add them back without also
  // restoring the write side, which is the part that could not be made correct.
  validateSearch: (search: Record<string, unknown>): {
    saleId?: string;
    companyId?: string;
    status?: 'available' | 'committed';
  } => ({
    saleId: typeof search.saleId === 'string' ? search.saleId : undefined,
    companyId: typeof search.companyId === 'string' ? search.companyId : undefined,
    status:
      search.status === 'available' || search.status === 'committed' ? search.status : undefined,
  }),
});

const newVehicleRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vehicles/new',
  component: () => <VehicleFormPage mode="create" />,
  beforeLoad: requireRoute('/vehicles/new'),
});
const vehicleDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vehicles/$vehicleId',
  component: () => <VehicleFormPage mode="edit" />,
  beforeLoad: requireRoute('/vehicles/$vehicleId'),
});

const firmsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/firms',
  component: FirmsPage,
  beforeLoad: requireRoute('/firms'),
});

const firmDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/firms/$companyId',
  component: FirmDetailPage,
  beforeLoad: requireRoute('/firms/$companyId'),
});

const usersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/users',
  component: UsersPage,
  beforeLoad: requireRoute('/users'),
});

const userDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/users/$userId',
  component: UserDetailPage,
  beforeLoad: requireRoute('/users/$userId'),
});

// Search params are validated so audit deep-links (from the user/sale pages) are typed + shareable.
const auditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/audit',
  component: AuditLogPage,
  beforeLoad: requireRoute('/audit'),
  validateSearch: (search: Record<string, unknown>): {
    entityType?: string;
    entityId?: string;
    actorId?: string;
    actorCompanyId?: string;
  } => ({
    entityType: typeof search.entityType === 'string' ? search.entityType : undefined,
    entityId: typeof search.entityId === 'string' ? search.entityId : undefined,
    actorId: typeof search.actorId === 'string' ? search.actorId : undefined,
    actorCompanyId: typeof search.actorCompanyId === 'string' ? search.actorCompanyId : undefined,
  }),
});

const configRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/config',
  component: ConfigPage,
  beforeLoad: requireRoute('/config'),
});

// Search params are validated so a colleague can be sent straight to a term or a procedure
// (`/help?tab=handbook&topic=h-live-hammer`), the same typed pattern the audit route uses.
const helpRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/help',
  component: HelpPage,
  beforeLoad: requireRoute('/help'),
  validateSearch: (search: Record<string, unknown>): {
    tab?: 'glossary' | 'handbook' | 'faq' | 'partner';
    topic?: string;
  } => ({
    tab:
      search.tab === 'glossary' ||
      search.tab === 'handbook' ||
      search.tab === 'faq' ||
      search.tab === 'partner'
        ? search.tab
        : undefined,
    topic: typeof search.topic === 'string' ? search.topic : undefined,
  }),
});

const submissionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/submissions',
  component: SubmissionsPage,
  beforeLoad: requireRoute('/submissions'),
});

// Inside `authedRoute`: the operator is signed in and entitled to be *here*, just not there,
// so the nav stays and the next click can go somewhere useful.
const forbiddenRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/forbidden',
  component: ForbiddenPage,
  beforeLoad: requireRoute('/forbidden'),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authedRoute.addChildren([
    indexRoute,
    salesListRoute,
    newSaleRoute,
    saleDetailRoute,
    vehiclesListRoute,
    newVehicleRoute,
    vehicleDetailRoute,
    firmsRoute,
    firmDetailRoute,
    usersRoute,
    userDetailRoute,
    auditRoute,
    configRoute,
    submissionsRoute,
    helpRoute,
    forbiddenRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
